/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: QueueFormView.tsx (Queues)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Queue form view component.
    Simplified to use hooks for data management and form state.

 🔧 Notes:
    - Uses useQueueData for fetching initial data
    - Uses useQueueForm for form state management
    - Renders form sections in tabs

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Phone, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { QueueGeneralSection } from './QueueGeneralSection';
import { QueueAnnouncementsSection } from './QueueAnnouncementsSection';
import { QueueAdvancedSection } from './QueueAdvancedSection';
import { QueueTiersSection } from './QueueTiersSection';
import { navigateToQueueDuplicate } from './queuesActions';
import { useQueueData, useQueueForm } from './hooks';
import { fetchQuickListQueues } from './api';
import { Strategy, TimeBaseScore } from './types';

export default function QueueFormView() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');

  const { mappedData, isLoading: isDataLoading } = useQueueData(id, duplicateId);

  const {
    formState,
    tierRows,
    extraSettings,
    errors,
    isSaving,
    hasChanges,
    handleFieldChange,
    handleTiersChange,
    handleExtraSettingsChange,
    handleSave,
    handleCancel,
  } = useQueueForm(id, duplicateId, mappedData);

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; uuid?: string; name: string; context?: string | null }>>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);

  const loadQuickList = useCallback(async () => {
    setIsQuickListLoading(true);
    try {
      const result = await fetchQuickListQueues();
      setQuickListItems(result);
    } catch {
      // Error handled by API layer, just set empty
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
    }
  }, []);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query) ||
      (item.context ?? '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSave();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id, uuid: id };
    }

    return savedData;
  }, [handleSave, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/call-center/queues',
    newPath: '/pbx/call-center/queues/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Queues use ID for navigation
      return `/pbx/call-center/queues/${savedData.uuid || savedData.id}`;
    },
    getFormData: () => ({
      formState,
      tierRows,
      extraSettings
    }),
    storageKey: 'queues:saveAndNewData'
  });

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToQueueDuplicate(navigate, id);
  }, [id, isEditMode, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/call-center/queues/new');
  }, [navigate]);

  return (
    <div className="p-6 pb-12 space-y-4">
      <ModuleFormHeader
        icon={<Phone className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('queues.form.editTitle')} - ${formState.queueName || ''}` : t('queues.form.createTitle')}
        backButtonText={t('common.list')}
        onBack={() => navigate(-1)}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('queues.actions.quickList'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('queues.actions.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('queues.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="space-y-4 pb-10">
        <Tabs defaultValue="general" className="space-y-4">
          <TabsList>
            <TabsTrigger value="general">{t('queues.form.tabs.general')}</TabsTrigger>
            <TabsTrigger value="announcements">{t('queues.form.tabs.announcements')}</TabsTrigger>
            <TabsTrigger value="advanced">{t('queues.form.tabs.advanced')}</TabsTrigger>
            <TabsTrigger value="tiers">{t('queues.form.tabs.tiers')}</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <QueueGeneralSection
              formData={formState}
              onFieldChange={handleFieldChange}
              onStrategyChange={(e) => handleFieldChange('strategy', e.target.value as Strategy)}
              onTimeBaseScoreChange={(e) => handleFieldChange('timeBaseScore', e.target.value as TimeBaseScore)}
            />
          </TabsContent>

          <TabsContent value="announcements" className="space-y-4">
            <QueueAnnouncementsSection
              formData={formState}
              onFieldChange={handleFieldChange}
            />
          </TabsContent>

          <TabsContent value="advanced" className="space-y-4">
            <QueueAdvancedSection
              formData={formState}
              onFieldChange={handleFieldChange}
              onTimeBaseScoreChange={(e) => handleFieldChange('timeBaseScore', e.target.value as TimeBaseScore)}
            />
          </TabsContent>

          <TabsContent value="tiers" className="space-y-4">
            <QueueTiersSection
              tiers={tierRows}
              onChange={handleTiersChange}
            />
          </TabsContent>
        </Tabs>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        isSaving={isSaving}
        isLoading={isDataLoading}
        isEditMode={isEditMode}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        useSaveDropdown={true}
        saveDropdownStorageKey="queues:saveActionPreference"
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
        }}
        title={t('queues.quickList.title', 'Queues')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/call-center/queues/${item.uuid ?? item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/call-center/queues/${item.uuid ?? item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
            {item.context && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.context}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('queues.quickList.searchPlaceholder', 'Search by name or context')}
        emptyMessage={t('queues.quickList.empty', 'No queues found')}
        headerIcon={<Phone className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
